// src/redux/services/esgnewmoduleactivites.js
import { createApi } from "@reduxjs/toolkit/query/react";
import apiBaseQuery from "./esgBaseQuery";

export const BASE_METRICS_API_REDUCER_KEY = "esgnew-moduleApi";

export const esgnewmoduleactivites = createApi({
  reducerPath: BASE_METRICS_API_REDUCER_KEY,
  baseQuery: apiBaseQuery,
  tagTypes: ["esgnew-module"],
  endpoints: (builder) => ({
    sustainabilityFocusAreas: builder.query({
      query: (params) => ({
        url: "/v1/sustainability-focus-areas",
        method: "GET",
        params,
      }),
      providesTags: ["esgnew-module"],
    }),

    uploadFormFile: builder.mutation({
      query: ({ formToken, company_id, reporting_period_json, file }) => {
        const formData = new FormData();
        formData.append("company_id", String(company_id));
        formData.append(
          "reporting_period_json",
          JSON.stringify(reporting_period_json)
        );
        formData.append("file", file);

        return {
          url: `/v1/form/${encodeURIComponent(formToken)}/upload-file`,
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ["esgnew-module"],
    }),

    submitExtractedData: builder.mutation({
      query: ({ formToken, accessToken, refreshToken, dataJson, files }) => {
        const formData = new FormData();
        formData.append("data_json", JSON.stringify(dataJson));

        if (Array.isArray(files)) {
          files.forEach((file) => formData.append("proof_files", file));
        }

        return {
          url: `/v1/form/${encodeURIComponent(
            formToken
          )}/submit-extracted-data`,
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ["esgnew-module"],
    }),

    esgChat: builder.mutation({
      query: (payload) => ({
        url: "/v1/esg-chat",
        method: "POST",

        body: {
          message: payload.message,
        },
      }),
    }),
    reportIssue: builder.mutation({
      query: (formData) => ({
        url: "/v1/report-issue",
        method: "POST",
        body: formData,
      }),
    }),

    metricSuggestions: builder.mutation({
      query: (payload) => ({
        url: "/v1/metric-suggestions",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["esgnew-module"],
    }),

    matchingInterface: builder.query({
      query: ({ formToken, session_id, params }) => ({
        url: `/v1/form/${encodeURIComponent(
          formToken
        )}/matching-interface/${encodeURIComponent(session_id)}`,
        method: "GET",
        params,
      }),
      providesTags: ["esgnew-module"],
    }),

    extractMetricsFromPdf: builder.mutation({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/v1/extract-metrics-from-pdf",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ["esgnew-module"],
    }),

    ingestMetrics: builder.mutation({
      query: (payload) => ({
        url: "/v1/ingest-metrics",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["esgnew-module"],
    }),

    pdfExtractedMetrics: builder.query({
      query: (params) => ({
        url: "/v1/pdf-extracted-metrics",
        method: "GET",
        params,
      }),
      providesTags: ["esgnew-module"],
    }),

    deletePdfExtractedMetric: builder.mutation({
      query: ({ id }) => ({
        url: `/v1/pdf-extracted-metrics/${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["esgnew-module"],
    }),

    updatePdfExtractedMetric: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/v1/pdf-extracted-metrics/${encodeURIComponent(id)}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["esgnew-module"],
    }),

    processSheetsAndColumns: builder.mutation({
      query: ({ formToken, session_id, sheet_mappings, headers }) => ({
        url: `/v1/form/${encodeURIComponent(
          formToken
        )}/process-sheets-and-columns`,
        method: "POST",
        body: {
          session_id,
          sheet_mappings,
        },
      }),
      invalidatesTags: ["esgnew-module"],
    }),
    downloadFormTemplate: builder.mutation({
      query: ({ formToken }) => ({
        url: `/v1/form/${encodeURIComponent(formToken)}/template`,
        method: "GET",
        responseType: "blob",
      }),
    }),
    ingestPdfExtraction: builder.mutation({
      query: (payload) => ({
        url: "/v1/ingest-pdf-extraction",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["esgnew-module"],
    }),
    metricWiseMetricsWithData: builder.query({
      query: () => ({
        url: "/v1/metric-wise/metrics/with-data",
        method: "GET",
      }),
      providesTags: ["esgnew-module"],
    }),

    createWidget: builder.mutation({
      query: (payload) => ({
        url: "/v1/custom-dashboard/widget",
        method: "POST",
        body: payload,
      }),
    }),

    getWidget: builder.query({
      query: () => ({
        url: "/v1/custom-dashboard/widget",
        method: "GET",
      }),
      providesTags: ["esgnew-module"],
    }),

    getWidgetWithData: builder.query({
      query: ({ start_date, end_date, node } = {}) => ({
        url: "/v1/custom-dashboard/widget/with-data",
        method: "GET",
        params: {
          start_date: start_date || null,
          end_date: end_date || null,
          company_id: node || null, 
        },
      }),
      providesTags: ["esgnew-module"],
    }),

    getWidgetData: builder.query({
      query: (widgetId) => ({
        url: `/v1/custom-dashboard/widget/${widgetId}/data`,
        method: "GET",
      }),
      providesTags: ["esgnew-module"],
    }),
    updateWidget: builder.mutation({
      query: (payload) => ({
        url: "/v1/custom-dashboard/widget",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Widget"],
    }),
    deleteWidget: builder.mutation({
      query: (widgetId) => ({
        url: `/v1/custom-dashboard/widget/${encodeURIComponent(widgetId)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["esgnew-module"],
    }),
  }),
});

export const {
  useSustainabilityFocusAreasQuery,
  useMetricSuggestionsMutation,
  useExtractMetricsFromPdfMutation,
  useGetWidgetQuery,
  useDeletePdfExtractedMetricMutation,
  useUpdatePdfExtractedMetricMutation,
  useIngestPdfExtractionMutation,
  useGetWidgetWithDataQuery,
  useUpdateWidgetMutation,
  useMatchingInterfaceQuery,
  useProcessSheetsAndColumnsMutation,
  useGetWidgetDataQuery,
  useReportIssueMutation,
  useEsgChatMutation,
  usePdfExtractedMetricsQuery,
  useMetricWiseMetricsWithDataQuery,
  useSubmitExtractedDataMutation,
  useDownloadFormTemplateMutation,
  useDeleteWidgetMutation,
  useIngestMetricsMutation,
  useCreateWidgetMutation,
  // NEW export:
  useUploadFormFileMutation,
} = esgnewmoduleactivites;
